import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import { Typography } from "@/components/Typography";

const styles = {
  card: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "20px",
    border: `1px solid ${theme.colors.staticFgLine}`,
    borderRadius: "12px",
    backgroundColor: theme.colors.white,
    transition: "box-shadow 120ms cubic-bezier(.2, .7, .2, 1)",
    "&:hover": {
      boxShadow: "0px 1px 3px rgba(16, 24, 40, 0.06)",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  logo: {
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "44px",
    minWidth: "44px",
    maxWidth: "104px",
    padding: "6px 8px",
    borderRadius: "8px",
    backgroundColor: theme.colors.neutral010,
    boxSizing: "border-box",
  },
  logoImage: {
    maxWidth: "88px",
    maxHeight: "32px",
    objectFit: "contain",
  },
  iconTile: {
    flex: "0 0 44px",
    width: "44px",
    height: "44px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.colors.staticBgPrimary,
    color: theme.colors.blue060,
    "& svg": {
      fontSize: "1.375rem",
    },
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px 8px",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    flex: 1,
  },
  meta: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px 16px",
  },
  infoBanner: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "8px 12px",
    borderRadius: "6px",
    backgroundColor: theme.colors.staticBgPrimary,
    color: theme.colors.staticFgPrimary,
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

type ButtonComponentProps = React.ComponentProps<typeof Button>;

export interface ApplicationCardProps {
  title: string;
  description: string;
  learnMoreLink?: string;
  learnMoreText?: string;
  buttonText: string;
  buttonVariant?: "primary" | "plain";
  onButtonClick?: () => void;
  buttonProps?: Partial<ButtonComponentProps>;
  /** A brand logo. Crescendo's own features pass `icon` instead. */
  imageUrl?: string;
  icon?: React.ReactNode;
  isConnected?: boolean;
  isLoading?: boolean;
  connectedLabel?: string;
  connectedButtonText?: string;
  infoBannerText?: string;
  infoBannerLink?: string;
  infoBannerIcon?: React.ReactNode;
  detailText?: string | undefined;
}

/**
 * One integration: its logo, name and status on one line, what it does, and a
 * single action at the bottom right. Connected items get a quieter "Manage"
 * so the setups still to do stand out.
 */
export const ApplicationCard: React.FC<ApplicationCardProps> =
  function ApplicationCard({
    title,
    description,
    learnMoreLink,
    learnMoreText,
    buttonText,
    buttonVariant = "primary",
    onButtonClick,
    buttonProps,
    imageUrl,
    icon,
    isConnected,
    isLoading,
    connectedLabel,
    connectedButtonText,
    infoBannerText,
    infoBannerLink,
    infoBannerIcon,
    detailText,
  }) {
    const { t } = useTranslation();
    const infoBanner: React.ReactNode = infoBannerText ? (
      <Box sx={styles.infoBanner}>
        {infoBannerIcon}
        {infoBannerText}
      </Box>
    ) : null;

    return (
      <Box component="section" aria-label={title} sx={styles.card}>
        <Box sx={styles.header}>
          {icon ? (
            <Box sx={styles.iconTile} aria-hidden="true">
              {icon}
            </Box>
          ) : imageUrl ? (
            <Box sx={styles.logo}>
              <Box
                component="img"
                src={imageUrl}
                alt=""
                sx={styles.logoImage}
              />
            </Box>
          ) : null}
          <Box sx={styles.titleBlock}>
            <Typography variant="h3" fontWeight={600}>
              {title}
            </Typography>
            {isConnected ? (
              <Badge variant="positive">{connectedLabel}</Badge>
            ) : null}
          </Box>
        </Box>
        <Box sx={styles.body}>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {description}
          </Typography>
          {detailText || (learnMoreLink && learnMoreText) ? (
            <Box sx={styles.meta}>
              {detailText ? (
                <Typography variant="note" color={theme.colors.staticFgNote}>
                  {detailText}
                </Typography>
              ) : null}
              {learnMoreLink && learnMoreText ? (
                <ExternalLink href={learnMoreLink} trailingIcon={true}>
                  {learnMoreText}
                </ExternalLink>
              ) : null}
            </Box>
          ) : null}
          {infoBanner && infoBannerLink ? (
            <ExternalLink href={infoBannerLink}>{infoBanner}</ExternalLink>
          ) : (
            infoBanner
          )}
        </Box>
        <Box sx={styles.footer}>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Button
              variant={isConnected ? "plain" : buttonVariant}
              onClick={onButtonClick}
              {...buttonProps}
            >
              {isConnected
                ? (connectedButtonText ?? t("common.edit"))
                : buttonText}
            </Button>
          )}
        </Box>
      </Box>
    );
  };
